/**
 * Deterministic AI Validation checks (Dizzaroo Regulatory Style Guide v1.0).
 * Sourced from: AI_Validation_Checks_Dizzaroo_Style, Template, Protocol and KG (1).xlsx
 */

import { VALIDATION_CHECK_CATALOG } from '../constants/validationCheckCatalog';
import {
  validateFontComplianceForExport,
  validateSIUnitsConsistencyForExport,
  validateTableNumberingForExport,
} from './m11Validation';
import { EXTENDED_RULE_CHECKS } from './aiValidationRulesExtended';
import { LAYOUT_REF_RULE_CHECKS } from './aiValidationRulesLayoutRefs';

const SENTENCE_START_ABBREV_EXCEPTIONS = new Set([
  'MEDDRA',
  'AIDS',
  'UNICEF',
  'NATO',
  'DNA',
  'RNA',
  'MRI',
  'CT',
  'ECG',
  'FDA',
  'EMA',
  'ICH',
  'GCP',
]);

const MONTH_NAMES =
  'January|February|March|April|May|June|July|August|September|October|November|December';

export const stripHtml = (html = '') => {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = html;
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const flattenSections = (sections = []) => {
  const flat = [];
  const walk = (section, depth = 0) => {
    if (!section) return;
    const html = section.output || section.content || '';
    flat.push({
      title: section.title || section.name || '',
      html,
      text: stripHtml(html),
      depth,
      sectionNumber: section.sectionNumber || '',
    });
    (section.subsections || []).forEach((sub) => walk(sub, depth + 1));
  };
  (sections || []).forEach((s) => walk(s, 0));
  return flat;
};

const getCombinedText = (flat) => flat.map((s) => s.text).filter(Boolean).join('\n\n');

const getCombinedHtml = (flat) => flat.map((s) => s.html).filter(Boolean).join('\n');

/** Text of whichever section(s) title-match the abbreviations glossary (same heuristic
 * as checkListOfAbbreviationsSection below). */
const getGlossaryText = (flat) =>
  flat
    .filter((s) => {
      const t = (s.title || '').toUpperCase();
      return t.includes('LIST OF ABBREVIATION') || t.includes('ABBREVIATIONS') || t.includes('LIST OF ABBREV');
    })
    .map((s) => s.text)
    .join('\n');

/** Abbreviation tokens the document's own glossary already defines. A glossary entry
 * satisfies ICH's "defined once" convention regardless of table/list formatting, so
 * checks below use this instead of requiring a redundant inline "Full Term (ABBR)". */
const getGlossaryDefinedAbbrs = (flat) => {
  const abbrs = new Set();
  const re = /\b[A-Z]{2,}\b/g;
  let m;
  const glossaryText = getGlossaryText(flat);
  while ((m = re.exec(glossaryText)) !== null) abbrs.add(m[0]);
  return abbrs;
};

/** Body prose only: strips tables (row/cell text isn't a "sentence") and headings
 * (which otherwise run straight into the next paragraph with no terminal punctuation,
 * e.g. "TRIAL POPULATION 5.1 Description..." reading as one heading+paragraph "sentence"). */
const getProseTextForSentenceChecks = (flat) =>
  flat
    .map((s) =>
      String(s.html || '')
        .replace(/<table[\s\S]*?<\/table>/gi, ' ')
        .replace(/<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>/gi, '. ')
        // A short bold lead-in opening a paragraph ("<p><strong>SAE Reporting to the
        // Sponsor</strong> …") is a run-in heading, not the start of a sentence.
        .replace(/(<p\b[^>]*>\s*)<strong>([^<]{1,90})<\/strong>/gi, '$1. ')
        .replace(/<[^>]+>/g, ' '),
    )
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

const limitErrors = (errors, max = 12) => {
  const list = Array.isArray(errors) ? errors : [];
  if (list.length <= max) return list;
  return [...list.slice(0, max), `…and ${list.length - max} more issue(s).`];
};

/** Abbreviations: Full Term (ABBR) at first use; flag bare ABBR before definition */
/** Shortest run of trailing words whose initials spell the abbreviation in order
 * ("…performed by computed tomography" → "computed tomography" for CT), so findings
 * quote the actual expansion instead of the up-to-7 words the definition regex grabs. */
const trimToExpansion = (term, abbr) => {
  const words = String(term || '').trim().split(/\s+/).filter(Boolean);
  for (let k = 1; k <= words.length; k += 1) {
    const tail = words.slice(words.length - k);
    const initials = tail
      .map((w) => w.split('-').map((part) => part.charAt(0)).join(''))
      .join('')
      .toUpperCase();
    let pos = 0;
    for (const ch of initials) if (ch === abbr[pos]) pos += 1;
    if (pos === abbr.length && initials.charAt(0) === abbr.charAt(0)) return tail.join(' ');
  }
  return words.join(' ');
};

/** Running text for first-use checks. Tables are excluded: the style guide has tables
 * define their abbreviations in their own footnotes, so a use inside a table is never
 * the running-text "first mention". */
const getRunningTextWithoutTables = (flat) =>
  flat
    .map((s) =>
      String(s.html || '')
        .replace(/<table[\s\S]*?<\/table>/gi, ' ')
        .replace(/<[^>]+>/g, ' '),
    )
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

export const checkAbbreviationFirstOccurrence = (flat) => {
  const errors = [];
  const text = getRunningTextWithoutTables(flat);
  const glossaryAbbrs = getGlossaryDefinedAbbrs(flat);
  // "(ABBR)" or "[ABBR]" — square brackets are the required form for a definition that
  // sits inside parentheses ("(high-resolution chest computed tomography [HRCT] …)").
  const defineRegex = /\b([A-Za-z][\w-]*(?:\s+[A-Za-z][\w-]*){0,6})\s*[([]([A-Z]{2,})[)\]]/g;
  const definitions = [];
  let m;
  while ((m = defineRegex.exec(text)) !== null) {
    definitions.push({ term: trimToExpansion(m[1], m[2]), abbr: m[2], index: m.index });
  }

  const definedAbbrs = new Map();
  definitions.forEach((d) => {
    if (!definedAbbrs.has(d.abbr) || d.index < definedAbbrs.get(d.abbr).index) {
      definedAbbrs.set(d.abbr, d);
    }
  });

  definedAbbrs.forEach((def, abbr) => {
    // Already listed in the document's own List of Abbreviations glossary — that
    // satisfies "defined" per ICH convention, so don't also demand an inline
    // "Full Term (ABBR)" restatement before every standalone use elsewhere.
    if (glossaryAbbrs.has(abbr)) return;
    const before = text.slice(0, def.index);
    const bareRegex = new RegExp(`\\b${abbr}\\b`, 'g');
    if (bareRegex.test(before)) {
      errors.push(
        `Abbreviation "${abbr}" appears before it is defined as "${def.term} (${abbr})".`
      );
    }
  });

  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Headings must not be only an abbreviation */
export const checkAbbreviationOnlyHeadings = (flat) => {
  const errors = [];
  const html = getCombinedHtml(flat);
  const headingRegex = /<h[1-6][^>]*>([^<]+)<\/h[1-6]>/gi;
  let m;
  while ((m = headingRegex.exec(html)) !== null) {
    const inner = stripHtml(m[1]).trim();
    if (/^[A-Z]{2,}$/.test(inner)) {
      errors.push(`Heading contains only abbreviation "${inner}" — spell out the full term in headings.`);
    }
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

// Uses at or above which a glossary-defined abbreviation counts as "a term used with
// very high frequency throughout the document" — the style pack's exception allowing
// it to start a sentence (Abbreviation rules block, "Sentence beginnings").
const HIGH_FREQUENCY_ABBR_MIN_USES = 10;

/** Sentences should not start with abbreviations (with known exceptions) */
export const checkAbbreviationSentenceStart = (flat) => {
  const errors = [];
  const glossaryAbbrs = getGlossaryDefinedAbbrs(flat);
  const hasGlossary = glossaryAbbrs.size > 0;
  // Reference entries are citations, not sentences ("AJCC Cancer Staging Manual.").
  const text = getProseTextForSentenceChecks(
    flat.filter((s) => !/references|bibliography|literature/i.test(s.title || '')),
  );
  const allText = getCombinedText(flat);
  const useCounts = new Map();
  const countUses = (token) => {
    if (!useCounts.has(token)) {
      useCounts.set(token, (allText.match(new RegExp(`\\b${token}\\b`, 'g')) || []).length);
    }
    return useCounts.get(token);
  };
  const sentences = text.split(/(?<=[.!?])\s+/);
  sentences.forEach((sentence) => {
    const trimmed = sentence.trim();
    const match = trimmed.match(/^([A-Z]{2,})\b/);
    if (!match) return;
    const token = match[1];
    if (SENTENCE_START_ABBREV_EXCEPTIONS.has(token)) return;
    // With a glossary present, only flag tokens it actually confirms as abbreviations —
    // otherwise ordinary all-caps words from headings/callouts ("TRIAL", "NOTE") or
    // compound product codes ("BGB-43395") get misidentified as unexpanded abbreviations.
    // Without a glossary there's no better signal, so fall back to the plain heuristic.
    if (hasGlossary && !glossaryAbbrs.has(token)) return;
    if (glossaryAbbrs.has(token) && countUses(token) >= HIGH_FREQUENCY_ABBR_MIN_USES) return;
    errors.push(`Sentence starts with abbreviation "${token}": "${trimmed.slice(0, 80)}…"`);
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Plural abbreviations: CRFs not CRF's */
export const checkAbbreviationPluralApostrophe = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const regex = /\b([A-Z]{2,})'s\b/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    errors.push(`Use plural "${m[1]}s" without apostrophe (found "${m[0]}").`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** List of Abbreviations section present */
export const checkListOfAbbreviationsSection = (flat) => {
  const hasLoa = flat.some((s) => {
    const t = (s.title || '').toUpperCase();
    return (
      t.includes('LIST OF ABBREVIATION') ||
      t.includes('ABBREVIATIONS') ||
      t.includes('LIST OF ABBREV')
    );
  });
  return {
    passed: hasLoa,
    errors: hasLoa ? [] : ['No "List of Abbreviations" section found in protocol sections.'],
  };
};

/** Do not use # for number */
export const checkHashAsNumber = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const regex = /#\s*\d+/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    errors.push(`Use "No." instead of "#" for number (found "${m[0]}").`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Consistent e.g./i.e. style (no mixing e.g. with eg,) */
export const checkEgIeConsistency = (flat) => {
  const text = getCombinedText(flat);
  // `\b` only binds to the first alternative in `\bA|B` — without grouping, the second
  // branch matched as a bare substring anywhere (e.g. "movie," or "cookie," falsely
  // tripped the "ie," comma-style branch below).
  const hasDotted = /\b(?:e\.g\.|i\.e\.)/i.test(text);
  const hasComma = /\b(?:eg|ie),/i.test(text);
  const errors = [];
  if (hasDotted && hasComma) {
    errors.push('Mixed citation styles: both "e.g./i.e." and "eg,/ie," appear. Use one style consistently.');
  }
  return { passed: errors.length === 0, errors };
};

/** Informal cross-references */
export const checkInformalCrossReferences = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const patterns = [
    /\bsee\s+(the\s+)?(table|figure|section)\s+(below|above|following)\b/gi,
    /\b(as\s+shown\s+in\s+the\s+(table|figure)\s+below)\b/gi,
    /\brefer\s+to\s+the\s+following\s+(table|figure|section)\b/gi,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      errors.push(`Informal cross-reference: "${m[0]}" — use "Table X", "Figure X", or "Section X.X".`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Cross-reference targets (Table N / Figure N) should exist in document */
export const checkCrossReferenceTargets = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const html = getCombinedHtml(flat);

  const tableLabels = new Set();
  const figureLabels = new Set();
  const labelRegex = /\b(Table|Figure)\s+(\d+(?:\.\d+)*)\b/gi;
  let m;
  while ((m = labelRegex.exec(html + text)) !== null) {
    const kind = m[1].toLowerCase();
    const num = m[2];
    if (kind === 'table') tableLabels.add(num);
    else figureLabels.add(num);
  }

  const refRegex = /\b(Table|Figure)\s+(\d+(?:\.\d+)*)\b/g;
  const refs = [];
  while ((m = refRegex.exec(text)) !== null) {
    refs.push({ kind: m[1].toLowerCase(), num: m[2], raw: m[0] });
  }

  const seen = new Set();
  refs.forEach((ref) => {
    const key = `${ref.kind}:${ref.num}`;
    if (seen.has(key)) return;
    seen.add(key);
    const exists =
      ref.kind === 'table' ? tableLabels.has(ref.num) : figureLabels.has(ref.num);
    if (!exists) {
      errors.push(`Cross-reference "${ref.raw}" — no matching ${ref.kind} label found in the document.`);
    }
  });

  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

// Oxford-comma detection moved to the Gemini semantic pass (ai-gemini-oxford-comma,
// see etmf-server aiValidation.service.js) — telling a real 3+-item series ("safety,
// efficacy and tolerability") apart from an ordinary 2-item conjunction that happens to
// follow an unrelated comma ("outcomes, safety and tolerability") needs actual sentence
// understanding; every regex heuristic tried here either missed real cases or flagged
// clause continuations, adjective pairs, and table-row artifacts as "series".

/** Straight (dumb) quotes in body text */
export const checkSmartQuotes = (flat) => {
  const text = getCombinedText(flat);
  const straightDouble = (text.match(/"/g) || []).length;
  const straightSingle = (text.match(/'/g) || []).length;
  const errors = [];
  if (straightDouble > 2) {
    errors.push(`Found ${straightDouble} straight double-quote character(s) — use curly quotes (“ ”).`);
  }
  if (straightSingle > 4) {
    errors.push(`Found ${straightSingle} straight apostrophe/single-quote character(s) — use curly apostrophes (’).`);
  }
  return { passed: errors.length === 0, errors };
};

/** US date order in running text (Month DD, YYYY) */
export const checkEuropeanDateFormat = (flat) => {
  const errors = [];
  // Running text only: AMA reference entries use "Published May 24, 2023. Accessed
  // October 31, 2023." by design, so the reference list is excluded.
  const text = getCombinedText(flat.filter((s) => !/references|bibliography|literature/i.test(s.title || '')));
  const usDate = new RegExp(`\\b(${MONTH_NAMES})\\s+\\d{1,2},?\\s+\\d{4}\\b`, 'gi');
  let m;
  while ((m = usDate.exec(text)) !== null) {
    errors.push(`US-style date "${m[0]}" — use European format (e.g., "31 October 2018").`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Time abbreviations (wk, mo, h, min) should not appear in running body text */
export const checkTimeAbbreviationsInBody = (flat) => {
  const errors = [];
  const textWithoutTables = getCombinedHtml(flat)
    .replace(/<table[\s\S]*?<\/table>/gi, ' ')
    .replace(/<[^>]+>/g, ' ');
  const regex = /\b\d+\s*(wk|mo|min|h|d|y)\b/gi;
  let m;
  while ((m = regex.exec(textWithoutTables)) !== null) {
    errors.push(
      `Time abbreviation "${m[0]}" in body text — spell out units in running text (tables/figures may use abbreviations).`
    );
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Sample size / N consistency across document */
export const checkSampleSizeConsistency = (flat) => {
  const text = getCombinedText(flat);
  const sizes = new Set();
  const patterns = [
    /\b(?:approximately|about|~)?\s*(\d{1,4})\s+(?:subjects|participants|patients)\b/gi,
    /\bN\s*=\s*(\d{1,4})\b/gi,
    /\bn\s*=\s*(\d{1,4})\b/g,
    /\bsample\s+size\s+(?:of\s+)?(\d{1,4})\b/gi,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      const n = parseInt(m[1], 10);
      if (n >= 10 && n <= 50000) sizes.add(n);
    }
  });
  const errors = [];
  // Part/cohort sizes that add up to the total are one consistent design, not a
  // conflict: 36 (Part 1a) + 18 + 22 (Part 1b cohorts) = 76 total.
  const sorted = [...sizes].sort((a, b) => b - a);
  const partsSumToTotal =
    sorted.length > 1 && sorted.slice(1).reduce((sum, n) => sum + n, 0) === sorted[0];
  if (partsSumToTotal) {
    return {
      passed: true,
      errors: [],
      successDetail: `Sample sizes are consistent: ${sorted.slice(1).join(' + ')} = ${sorted[0]} total.`,
    };
  }
  if (sizes.size > 3) {
    errors.push(
      `Multiple distinct sample sizes detected (${[...sizes].join(', ')}). Verify consistency across synopsis, design, and statistics sections.`
    );
  }
  return { passed: errors.length === 0, errors };
};

export const RULE_CHECKS = [
  {
    id: 'ai-abbrev-first-use',
    label: 'Abbreviations: First Use as Full Term (ABBR)',
    category: 'Abbreviation & Terminology',
    run: checkAbbreviationFirstOccurrence,
  },
  {
    id: 'ai-abbrev-heading-only',
    label: 'Abbreviations: Not Sole Heading',
    category: 'Abbreviation & Terminology',
    run: checkAbbreviationOnlyHeadings,
  },
  {
    id: 'ai-abbrev-sentence-start',
    label: 'Abbreviations: Sentence Start',
    category: 'Abbreviation & Terminology',
    run: checkAbbreviationSentenceStart,
  },
  {
    id: 'ai-abbrev-plural',
    label: 'Abbreviations: Plural Without Apostrophe',
    category: 'Abbreviation & Terminology',
    run: checkAbbreviationPluralApostrophe,
  },
  {
    id: 'ai-abbrev-loa',
    label: 'List of Abbreviations Section Present',
    category: 'Abbreviation & Terminology',
    run: checkListOfAbbreviationsSection,
  },
  {
    id: 'ai-style-hash-number',
    label: 'Formatting: No # for Number',
    category: 'Formatting & Style',
    run: checkHashAsNumber,
  },
  {
    id: 'ai-style-eg-ie',
    label: 'Formatting: e.g./i.e. Style Consistency',
    category: 'Formatting & Style',
    run: checkEgIeConsistency,
  },
  {
    id: 'ai-xref-informal',
    label: 'Cross-References: Formal Wording',
    category: 'Cross-References',
    run: checkInformalCrossReferences,
  },
  {
    id: 'ai-xref-targets',
    label: 'Cross-References: Table/Figure Targets Exist',
    category: 'Cross-References',
    run: checkCrossReferenceTargets,
  },
  {
    id: 'ai-punct-quotes',
    label: 'Punctuation: Curly Quotes & Apostrophes',
    category: 'Punctuation',
    run: checkSmartQuotes,
  },
  {
    id: 'ai-punct-dates',
    label: 'Punctuation: European Date Format',
    category: 'Punctuation',
    run: checkEuropeanDateFormat,
  },
  {
    id: 'ai-time-abbrev-body',
    label: 'Terminology: Time Units Spelled Out in Body',
    category: 'Abbreviation & Terminology',
    run: checkTimeAbbreviationsInBody,
  },
  {
    id: 'ai-numeric-sample-size',
    label: 'Numeric Consistency: Sample Size',
    category: 'Administrative',
    run: checkSampleSizeConsistency,
  },
  ...EXTENDED_RULE_CHECKS,
  ...LAYOUT_REF_RULE_CHECKS,
];

export const runDeterministicAiValidationRules = (sections = []) => {
  const flat = flattenSections(sections);
  const results = [];

  RULE_CHECKS.forEach(({ id, label, category, run }) => {
    // successDetail (why a check passed, e.g. "TOC generated at export") and skipped
    // (check not applicable) were previously dropped here, so every pass showed the
    // generic catalog text and skipped checks showed as passed.
    const { passed, errors, successDetail, skipped } = run(flat);
    const meta = VALIDATION_CHECK_CATALOG[id] || {};
    results.push({
      id,
      label,
      category,
      passed,
      skipped: Boolean(skipped),
      errors: passed && !skipped ? [] : errors,
      successDetail,
      methodology: 'rule',
      howChecked: meta.howChecked,
      passCriteria: meta.passCriteria,
    });
  });

  const pushWithMeta = (id, label, category, passed, errors) => {
    const meta = VALIDATION_CHECK_CATALOG[id] || {};
    results.push({
      id,
      label,
      category,
      passed,
      errors: passed ? [] : errors,
      methodology: 'rule',
      howChecked: meta.howChecked,
      passCriteria: meta.passCriteria,
    });
  };

  const fontResult = validateFontComplianceForExport(sections);
  pushWithMeta(
    'ai-style-tnr-font',
    'Formatting: Times New Roman (Style Guide §2)',
    'Formatting & Style',
    fontResult.isValid,
    fontResult.errors || []
  );

  const tableResult = validateTableNumberingForExport(sections);
  pushWithMeta(
    'ai-table-numbering',
    'Tables: Sequential Numbering',
    'Tables & Figures',
    tableResult.isValid,
    tableResult.errors || []
  );

  const siResult = validateSIUnitsConsistencyForExport(sections);
  pushWithMeta(
    'ai-si-units',
    'Formatting: SI Units Consistency',
    'Formatting & Style',
    siResult.isValid,
    siResult.errors || []
  );

  return results;
};
